/**
 * spaceQueries.js
 * Place at: src/lib/spaceQueries.js
 *
 * FIXED: createSpace now uses increment_moderated_spaces_count RPC
 * instead of the broken supabase.rpc() passed as a value to .update()
 */

import { supabase } from "../supabase";

export const SPACE_PAGE_SIZE = 10;

// ─── METADATA ─────────────────────────────────────────────────────────────────

export async function fetchSpaceMetadata(spaceId, userId) {
  const { data, error } = await supabase.rpc("get_space_metadata", {
    p_space_id: spaceId,
    p_user_id:  userId,
  });
  if (error) throw error;
  return data;
}

export async function searchSpaces(query, userId, limit = 10) {
  const { data, error } = await supabase.rpc("search_spaces", {
    p_query:   query || null,
    p_user_id: userId,
    p_limit:   limit,
  });
  if (error) throw error;
  return data || [];
}

export async function fetchTopSpacesForFeed() {
  const { data, error } = await supabase.rpc("get_top_spaces_for_feed");
  if (error) throw error;
  return data || [];
}

export async function checkModeratorEligibility(userId) {
  const { data, error } = await supabase
    .from("feed_users")
    .select("follower_count, moderated_spaces_count, is_admin, is_banned, created_at")
    .eq("id", userId)
    .single();
  if (error) throw error;

  if (data.is_admin)  return { eligible: true, reason: "admin" };
  if (data.is_banned) return { eligible: false, reason: "Your account is suspended" };
  if (data.moderated_spaces_count >= 3) return { eligible: false, reason: "You can moderate a maximum of 3 spaces" };

  const daysOld = Math.floor((Date.now() - new Date(data.created_at).getTime()) / 86400000);
  if (daysOld < 30) return { eligible: false, reason: `Your account needs to be ${30 - daysOld} more days old` };

  if (data.follower_count < 50) return {
    eligible: false,
    reason: `You need ${50 - data.follower_count} more followers to create a space`,
  };

  return { eligible: true, reason: "ok" };
}

// ─── SPACE FEED ───────────────────────────────────────────────────────────────

export async function fetchSpaceFeedPage(spaceId, userId, { page = 0, sort = "new", pageSize = SPACE_PAGE_SIZE, signal } = {}) {
  const from = page * pageSize;
  const to   = from + pageSize - 1;

  const { data, error } = await supabase.rpc("get_space_feed_with_user_state", {
    p_space_id: spaceId,
    p_user_id:  userId,
    p_from:     from,
    p_to:       to,
    p_sort:     sort,
  }, { signal });

  if (error) throw error;

  const posts = (data || []).filter(p => !p.is_deleted && !p.is_hidden);
  const voteMap = {}, bookmarkMap = {}, reportMap = {};
  posts.forEach(p => {
    if (p.my_vote)       voteMap[p.id]     = p.my_vote;
    if (p.is_bookmarked) bookmarkMap[p.id] = true;
    if (p.is_reported)   reportMap[p.id]   = true;
  });
  return { posts, voteMap, bookmarkMap, reportMap };
}

export async function fetchPendingPosts(spaceId, actorId) {
  const { data, error } = await supabase.rpc("get_pending_space_posts", {
    p_space_id: spaceId,
  });
  if (error) throw error;
  return data || [];
}

// ─── MEMBERSHIP ───────────────────────────────────────────────────────────────

export async function joinSpace(spaceId, userId) {
  const { data, error } = await supabase.rpc("join_space", {
    p_space_id: spaceId,
    p_user_id:  userId,
  });
  if (error) throw error;
  return data;
}

export async function leaveSpace(spaceId, userId) {
  const { error } = await supabase.rpc("leave_space", {
    p_space_id: spaceId,
    p_user_id:  userId,
  });
  if (error) throw error;
}

// ─── SPACE CREATION ───────────────────────────────────────────────────────────

export async function createSpace(userId, { name, description, rules, tag, avatarEmoji, color, weeklyPrompt, isPrivate = false }) {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Math.random().toString(36).slice(2, 6);

  const { data: space, error: spaceError } = await supabase
    .from("spaces")
    .insert({
      name:                 name.trim(),
      slug,
      description:          description.trim(),
      rules:                rules || [],
      tag:                  tag   || null,
      avatar_emoji:         avatarEmoji || "🏡",
      color:                color       || "#6366f1",
      weekly_prompt:        weeklyPrompt?.trim() || null,
      weekly_prompt_set_at: weeklyPrompt?.trim() ? new Date().toISOString() : null,
      moderator_id:         userId,
      moderator_username:   (await supabase.from("feed_users").select("username").eq("id", userId).single()).data?.username,
      created_by:           userId,
      member_count:         1,
      post_count:           0,
      is_private:           isPrivate,
    })
    .select()
    .single();

  if (spaceError) throw spaceError;

  // Add creator as moderator member
  await supabase.from("space_members").insert({
    space_id:  space.id,
    user_id:   userId,
    role:      "moderator",
    joined_at: new Date().toISOString(),
  });

  // FIX: was supabase.rpc() passed as a value to .update() — now uses proper RPC
  await supabase.rpc("increment_moderated_spaces_count", { p_user_id: userId });

  return space;
}

// ─── MODERATOR ACTIONS ────────────────────────────────────────────────────────

export async function removeSpaceMember(spaceId, userId, actorId) {
  const { error } = await supabase.rpc("remove_space_member", {
    p_space_id: spaceId,
    p_user_id:  userId,
    p_actor_id: actorId,
  });
  if (error) throw error;
}

export async function muteSpaceMember(spaceId, userId, days, actorId) {
  const { error } = await supabase.rpc("mute_space_member", {
    p_space_id: spaceId,
    p_user_id:  userId,
    p_days:     days,
    p_actor_id: actorId,
  });
  if (error) throw error;
}

export async function pinSpacePost(spaceId, postId, actorId) {
  const { error } = await supabase.rpc("pin_space_post", {
    p_space_id: spaceId,
    p_post_id:  postId,
    p_actor_id: actorId,
  });
  if (error) throw error;
}

export async function unpinSpacePost(spaceId, postId, actorId) {
  const { error } = await supabase.rpc("unpin_space_post", {
    p_space_id: spaceId,
    p_post_id:  postId,
    p_actor_id: actorId,
  });
  if (error) throw error;
}

export async function removePostFromSpace(spaceId, postId, actorId) {
  const { error } = await supabase.rpc("remove_post_from_space", {
    p_space_id: spaceId,
    p_post_id:  postId,
    p_actor_id: actorId,
  });
  if (error) throw error;
}

export async function promoteComoderator(spaceId, userId, actorId) {
  const { error } = await supabase.rpc("promote_comoderator", {
    p_space_id: spaceId,
    p_user_id:  userId,
    p_actor_id: actorId,
  });
  if (error) throw error;
}

export async function revokeComoderator(spaceId, userId, actorId) {
  const { error } = await supabase.rpc("revoke_comoderator", {
    p_space_id: spaceId,
    p_user_id:  userId,
    p_actor_id: actorId,
  });
  if (error) throw error;
}

export async function setWeeklyPrompt(spaceId, prompt, actorId) {
  const { error } = await supabase.rpc("set_weekly_prompt", {
    p_space_id: spaceId,
    p_prompt:   prompt,
    p_actor_id: actorId,
  });
  if (error) throw error;
}

export async function toggleModeratedMode(spaceId, actorId) {
  const { data, error } = await supabase.rpc("toggle_moderated_mode", {
    p_space_id: spaceId,
    p_actor_id: actorId,
  });
  if (error) throw error;
  return data;
}

export async function approveSpacePost(spaceId, postId, actorId) {
  const { error } = await supabase.rpc("approve_space_post", {
    p_space_id: spaceId,
    p_post_id:  postId,
    p_actor_id: actorId,
  });
  if (error) throw error;
}

// ─── MOD LOG ──────────────────────────────────────────────────────────────────

export async function fetchModLog(spaceId, limit = 30) {
  const { data, error } = await supabase
    .from("space_mod_log")
    .select("id, actor_username, action, target_type, detail, created_at")
    .eq("space_id", spaceId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function fetchSpaceMembers(spaceId, limit = 50) {
  const { data: members, error } = await supabase
    .from("space_members")
    .select("user_id, role, joined_at, muted_until")
    .eq("space_id", spaceId)
    .not("role", "eq", "banned")
    .order("joined_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  if (!members?.length) return [];

  const ids = members.map(m => m.user_id);
  const { data: users } = await supabase
    .from("feed_users")
    .select("id, username, follower_count")
    .in("id", ids);

  const userMap = {};
  (users || []).forEach(u => { userMap[u.id] = u; });

  return members.map(m => ({
    ...m,
    feed_users: userMap[m.user_id] || { username: m.user_id, follower_count: 0 },
  }));
}

// ─── USERNAME CHANGE ──────────────────────────────────────────────────────────

export async function changeUsername(newUsername) {
  const { data, error } = await supabase.rpc("change_username", {
    p_new_username: newUsername,
  });
  if (error) throw error;
  return data;
}

// ─── PENDING MEMBERS ──────────────────────────────────────────────────────────

export async function fetchPendingMembers(spaceId, actorId) {
  const { data, error } = await supabase.rpc("get_pending_members", {
    p_space_id: spaceId,
    p_actor_id: actorId,
  });
  if (error) throw error;
  return data || [];
}

export async function approvePendingMember(spaceId, userId, actorId) {
  const { error } = await supabase.rpc("approve_pending_member", {
    p_space_id: spaceId,
    p_user_id:  userId,
    p_actor_id: actorId,
  });
  if (error) throw error;
}

export async function rejectPendingMember(spaceId, userId, actorId) {
  const { error } = await supabase.rpc("reject_pending_member", {
    p_space_id: spaceId,
    p_user_id:  userId,
    p_actor_id: actorId,
  });
  if (error) throw error;
}

// ─── DELETE SPACE ─────────────────────────────────────────────────────────────

export async function deleteSpace(spaceId, actorId) {
  const { error } = await supabase.rpc("delete_space", {
    p_space_id: spaceId,
    p_actor_id: actorId,
  });
  if (error) throw error;
}